import { useProducts } from "@/lib/hooks/products/useProducts";
import type { ProductLevelScheme } from "@/lib/utils/productLevel";
import { Button, LoadMoreButton, Modal, SearchBar } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";

export interface ProductOption extends ProductLevelScheme {
  id: string;
  name: string;
}

export interface ProductPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
  onToggle: (product: ProductOption) => void;
}

export function ProductPickerModal({
  isOpen,
  onClose,
  selectedIds,
  onToggle,
}: ProductPickerModalProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { products, loading, loadMore, hasMore } = useProducts(debouncedSearch);

  const handleClose = () => {
    setSearch("");
    setDebouncedSearch("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Select Products">
      <div className="space-y-3">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search for product"
          className="w-full"
        />

        {loading && products.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <span className="text-sm text-secondary-400">Loading products...</span>
          </div>
        ) : products.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <span className="text-sm text-secondary-400">No products found</span>
          </div>
        ) : (
          <div className="space-y-1 max-h-[45vh] overflow-y-auto gradient-scrollbar">
            {products.map((product) => (
              <label
                key={product.id}
                className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                  selectedIds.includes(product.id)
                    ? "bg-primary-500/10 border border-primary-500/30"
                    : "border border-transparent hover:bg-gray-800/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(product.id)}
                  onChange={() => onToggle(product)}
                  className="sr-only"
                />
                {selectedIds.includes(product.id) ? (
                  <MdCheckBox className="w-5 h-5 text-success-500" />
                ) : (
                  <MdCheckBoxOutlineBlank className="w-5 h-5 text-secondary-400" />
                )}
                <span className="text-sm text-secondary-200">{product.name}</span>
              </label>
            ))}
          </div>
        )}

        {hasMore && (
          <LoadMoreButton
            onClick={loadMore}
            hasMore={hasMore}
            loading={loading}
            defaultText="Load more"
          />
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-800">
          <span className="text-sm text-secondary-400">{selectedIds.length} selected</span>
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
